<template>
  <div class="duration-hm-input formulate-input">
    <label v-if="label" :for="id" class="formulate-input-label">{{ label }}</label>
    <div class="duration-hm-container formulate-input-element">
      <div class="time-segment">
        <button
          type="button"
          class="time-btn time-btn-up"
          :disabled="disabled || hours >= maxHours"
          @click="incrementHours"
        >
          <feather-icon icon="ChevronUpIcon" size="14" />
        </button>
        <input
          :id="`${id}-hours`"
          type="text"
          :value="hoursDisplay"
          @input="handleHoursInput"
          @blur="handleHoursBlur"
          @focus="handleHoursFocus"
          :disabled="disabled"
          class="time-input"
          maxlength="2"
        />
        <button
          type="button"
          class="time-btn time-btn-down"
          :disabled="disabled || hours <= 0"
          @click="decrementHours"
        >
          <feather-icon icon="ChevronDownIcon" size="14" />
        </button>
      </div>
      <span class="time-separator">:</span>
      <div class="time-segment">
        <button
          type="button"
          class="time-btn time-btn-up"
          :disabled="disabled || minutes >= 59"
          @click="incrementMinutes"
        >
          <feather-icon icon="ChevronUpIcon" size="14" />
        </button>
        <input
          :id="`${id}-minutes`"
          type="text"
          :value="minutesDisplay"
          @input="handleMinutesInput"
          @blur="handleMinutesBlur"
          @focus="handleMinutesFocus"
          :disabled="disabled"
          class="time-input"
          maxlength="2"
        />
        <button
          type="button"
          class="time-btn time-btn-down"
          :disabled="disabled || minutes <= 0"
          @click="decrementMinutes"
        >
          <feather-icon icon="ChevronDownIcon" size="14" />
        </button>
      </div>
      <span class="time-separator">:</span>
      <div class="time-segment">
        <button
          type="button"
          class="time-btn time-btn-up"
          :disabled="disabled || seconds >= 59"
          @click="incrementSeconds"
        >
          <feather-icon icon="ChevronUpIcon" size="14" />
        </button>
        <input
          :id="`${id}-seconds`"
          type="text"
          :value="secondsDisplay"
          @input="handleSecondsInput"
          @blur="handleSecondsBlur"
          @focus="handleSecondsFocus"
          :disabled="disabled"
          class="time-input"
          maxlength="2"
        />
        <button
          type="button"
          class="time-btn time-btn-down"
          :disabled="disabled || seconds <= 0"
          @click="decrementSeconds"
        >
          <feather-icon icon="ChevronDownIcon" size="14" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DurationHmInput",
  props: {
    value: {
      type: [String, Object],
      default: null,
    },
    name: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxHours: {
      type: Number,
      default: 24,
    },
    context: {
      type: Object,
      default: null,
    },
    onUpdateValue: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      hours: 0,
      minutes: 0,
      seconds: 0,
      hoursEditing: false,
      minutesEditing: false,
      secondsEditing: false,
    };
  },
  computed: {
    id() {
      return `duration-hm-${this._uid}`;
    },
    hoursDisplay() {
      if (this.hoursEditing) {
        return this.hours === 0 ? "" : String(this.hours);
      }
      return String(this.hours).padStart(2, "0");
    },
    minutesDisplay() {
      if (this.minutesEditing) {
        return this.minutes === 0 ? "" : String(this.minutes);
      }
      return String(this.minutes).padStart(2, "0");
    },
    secondsDisplay() {
      if (this.secondsEditing) {
        return this.seconds === 0 ? "" : String(this.seconds);
      }
      return String(this.seconds).padStart(2, "0");
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        console.log(`[DurationHmsInput] Value changed for ${this.name}:`, newVal);
        this.parseValue(newVal);
      },
    },
    'context.model': {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal !== undefined && newVal !== null) {
          const currentValue = { hours: this.hours, minutes: this.minutes, seconds: this.seconds };
          const newValue = typeof newVal === 'object' ? newVal : null;
          if (newValue && (newValue.hours !== currentValue.hours || newValue.minutes !== currentValue.minutes || newValue.seconds !== currentValue.seconds)) {
            console.log(`[DurationHmsInput] Context model changed for ${this.name}:`, newVal);
            this.parseValue(newVal);
          }
        }
      },
    },
  },
  mounted() {
    console.log(`[DurationHmsInput] Mounted with name: ${this.name}, value:`, this.value, 'context:', this.context, 'parent:', this.$parent);
    
    let initialValue = null;
    if (this.context && this.context.model !== undefined && this.context.model !== null) {
      initialValue = this.context.model;
    } else if (this.value !== undefined && this.value !== null) {
      initialValue = this.value;
    } else {
      let parent = this.$parent;
      let attempts = 0;
      while (parent && attempts < 5) {
        if (parent.model && typeof parent.model === 'object' && parent.model[this.name] !== undefined) {
          initialValue = parent.model[this.name];
          console.log(`[DurationHmsInput] Found value in parent.model[${this.name}]:`, initialValue);
          break;
        }
        parent = parent.$parent;
        attempts++;
      }
    }
    
    if (initialValue !== null) {
      this.parseValue(initialValue);
    } else {
      this.hours = 0;
      this.minutes = 0;
      this.seconds = 0;
    }
  },
  methods: {
    parseValue(val) {
      if (!val) {
        this.hours = 0;
        this.minutes = 0;
        this.seconds = 0;
        return;
      }

      if (typeof val === "object") {
        this.hours = Number(val.hours) || 0;
        this.minutes = Number(val.minutes) || 0;
        this.seconds = Number(val.seconds) || 0;
        return;
      }

      if (typeof val === "string") {
        const parts = val.split(":");
        if (parts.length >= 2) {
          this.hours = Number(parts[0]) || 0;
          this.minutes = Number(parts[1]) || 0;
          this.seconds = parts.length >= 3 ? (Number(parts[2]) || 0) : 0;
          return;
        }
      }

      this.hours = 0;
      this.minutes = 0;
      this.seconds = 0;
    },
    normalizeTime() {
      if (this.seconds > 59) {
        const extraMinutes = Math.floor(this.seconds / 60);
        this.minutes += extraMinutes;
        this.seconds = this.seconds % 60;
      }
      if (this.seconds < 0) {
        this.seconds = 0;
      }

      if (this.minutes > 59) {
        const extraHours = Math.floor(this.minutes / 60);
        this.hours += extraHours;
        this.minutes = this.minutes % 60;
      }
      if (this.minutes < 0) {
        this.minutes = 0;
      }

      const totalSeconds = this.hours * 3600 + this.minutes * 60 + this.seconds;
      const maxTotalSeconds = this.maxHours * 3600;
      if (totalSeconds > maxTotalSeconds) {
        this.hours = this.maxHours;
        this.minutes = 0;
        this.seconds = 0;
      }

      if (this.hours < 0) {
        this.hours = 0;
      }
      if (this.hours > this.maxHours) {
        this.hours = this.maxHours;
        this.minutes = 0;
        this.seconds = 0;
      }
    },
    findFormModel() {
      let current = this.$parent;
      let depth = 0;
      while (current && depth < 15) {
        if (current.model && typeof current.model === 'object' && current.model !== null && !Array.isArray(current.model)) {
          if (current.$options && current.$options.name === 'FormulateForm') {
            return current.model;
          }
          if (current.$parent && current.$parent.$options && current.$parent.$options.name === 'FormulateForm') {
            return current.$parent.model;
          }
        }
        current = current.$parent;
        depth++;
      }
      return null;
    },
    emitValue(skipLog = false) {
      this.normalizeTime();
      const value = {
        hours: this.hours,
        minutes: this.minutes,
        seconds: this.seconds,
      };
      if (!skipLog) {
        console.log(`[DurationHmsInput] Emitting value for ${this.name}:`, value);
      }
      
      if (this.onUpdateValue) {
        this.onUpdateValue(value);
        if (!skipLog) {
          console.log(`[DurationHmsInput] Called onUpdateValue for ${this.name}`);
        }
      }
      
      if (this.context && this.context.model !== undefined) {
        this.$set(this.context, 'model', value);
        if (!skipLog) {
          console.log(`[DurationHmsInput] Updated context.model for ${this.name}:`, this.context.model);
        }
      }
      
      this.$emit("input", value);
    },
    incrementHours() {
      if (this.hours < this.maxHours) {
        this.hours++;
        this.emitValue();
      }
    },
    decrementHours() {
      if (this.hours > 0) {
        this.hours--;
        this.emitValue();
      }
    },
    incrementMinutes() {
      if (this.minutes < 59) {
        this.minutes++;
        this.emitValue();
      } else {
        if (this.hours < this.maxHours) {
          this.hours++;
          this.minutes = 0;
          this.emitValue();
        }
      }
    },
    decrementMinutes() {
      if (this.minutes > 0) {
        this.minutes--;
        this.emitValue();
      } else {
        if (this.hours > 0) {
          this.hours--;
          this.minutes = 59;
          this.emitValue();
        }
      }
    },
    incrementSeconds() {
      if (this.seconds < 59) {
        this.seconds++;
        this.emitValue();
      } else {
        if (this.minutes < 59) {
          this.minutes++;
          this.seconds = 0;
          this.emitValue();
        } else {
          if (this.hours < this.maxHours) {
            this.hours++;
            this.minutes = 0;
            this.seconds = 0;
            this.emitValue();
          }
        }
      }
    },
    decrementSeconds() {
      if (this.seconds > 0) {
        this.seconds--;
        this.emitValue();
      } else {
        if (this.minutes > 0) {
          this.minutes--;
          this.seconds = 59;
          this.emitValue();
        } else {
          if (this.hours > 0) {
            this.hours--;
            this.minutes = 59;
            this.seconds = 59;
            this.emitValue();
          }
        }
      }
    },
    handleHoursFocus() {
      this.hoursEditing = true;
    },
    handleMinutesFocus() {
      this.minutesEditing = true;
    },
    handleHoursInput(event) {
      const val = event.target.value.replace(/\D/g, "");
      if (val === "") {
        this.hours = 0;
      } else {
        const num = Number(val);
        this.hours = Math.min(num, this.maxHours);
      }
      this.emitValue();
    },
    handleHoursBlur() {
      this.hoursEditing = false;
      this.normalizeTime();
      this.emitValue();
      this.$forceUpdate();
    },
    handleMinutesInput(event) {
      const val = event.target.value.replace(/\D/g, "");
      if (val === "") {
        this.minutes = 0;
      } else {
        const num = Number(val);
        this.minutes = Math.min(num, 59);
      }
      this.emitValue();
    },
    handleMinutesBlur() {
      this.minutesEditing = false;
      this.normalizeTime();
      this.emitValue();
      this.$forceUpdate();
    },
    handleSecondsFocus() {
      this.secondsEditing = true;
    },
    handleSecondsInput(event) {
      const val = event.target.value.replace(/\D/g, "");
      if (val === "") {
        this.seconds = 0;
      } else {
        const num = Number(val);
        this.seconds = Math.min(num, 59);
      }
      this.emitValue();
    },
    handleSecondsBlur() {
      this.secondsEditing = false;
      this.normalizeTime();
      this.emitValue();
      this.$forceUpdate();
    },
  },
};
</script>

<style scoped>
.duration-hm-input {
  width: 100%;
  margin-bottom: 12px;
}

.formulate-input-label {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
  color: #5e5873;
}

.duration-hm-container {
  display: inline-flex;
  align-items: center;
  gap: 0;
  border: 1px solid #d8d6de;
  border-radius: 6px;
  padding: 4px 8px;
  background: #fff;
  transition: all 0.2s;
}

.duration-hm-container:focus-within {
  border-color: #7367f0;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.1);
}

.time-segment {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.time-input {
  width: 42px;
  text-align: center;
  border: none;
  background: transparent;
  padding: 4px 2px;
  font-size: 16px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  outline: none;
}

.time-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.time-btn {
  width: 24px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s;
  color: #6e6b7b;
}

.time-btn:hover:not(:disabled) {
  background-color: #f8f8f8;
  color: #7367f0;
}

.time-btn:active:not(:disabled) {
  background-color: #e8e6f0;
}

.time-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.time-separator {
  font-size: 18px;
  font-weight: 600;
  color: #6e6b7b;
  margin: 0 4px;
  line-height: 1;
  user-select: none;
}
</style>
